import {
  Box,
  Button,
  type ComboboxData,
  type ComboboxItem
} from "@mantine/core";
import ReactDOM from "react-dom";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useComponentsBase } from "../../../../hooks/useComponentsBase";
import { useConfiguratorComponents } from "../../../../hooks/useConfiguratorComponents";
import type { Tag } from "../../../../hooks/useEnergyDataAcquisitionTags";
import { usePersons } from "../../../../hooks/usePersons";
import { DateRangePresets } from "../../../../mantine/dates/DateRangeInput/DateRangeInput.constants";
import { useToast } from "../../../../mantine/notifications/useToast";
import { sortComboboxItems } from "../../../../mantine/utils/sortComboboxItems";
import { ObjectName } from "../../../../utils/enums";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type { NumericComboboxItem } from "../../../BuildingBlocks/Forms/MantineFormFields/fields/FormFieldSelect";
import { setErrorsFromResponseData } from "../../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type { Identity } from "../../EdaTable/Mbk";
import {
  CREATE_EDA_VERSION_FORM_FIELD_DATA,
  EDA_FORM_FIELDS,
  ESTIMATION_PROCEDURE_FIELDS
} from "../CreateEdaVersionModal.constants";
import type {
  EdaVersionFormValues,
  IdentityDefinitionList,
  PrefillData
} from "../CreateEdaVersionModal.types";
import {
  ESTIMATION_PROCEDURE_PARAMETER_ANNUAL_CONSUMPTION,
  ESTIMATION_PROCEDURE_PARAMETER_COMPUTATION,
  ESTIMATION_PROCEDURE_PARAMETER_CONSTANT_LOAD_KW,
  ESTIMATION_PROCEDURE_PARAMETER_FREQUENCY,
  ESTIMATION_PROCEDURE_PARAMETER_NUMBER_OF_CONSUMERS,
  ESTIMATION_PROCEDURE_PARAMETER_RATIO_OF_ENERGY_DATA,
  ESTIMATION_PROCEDURE_PARAMETER_REFERENCED_EDA_IDENTITY,
  ESTIMATION_PROCEDURE_PARAMETER_SECURITY_BUFFER,
  ORIGIN_COMPUTATION,
  ORIGIN_ESTIMATION,
  ORIGIN_METER
} from "../CreateEdaVersionModal.types";
import { DYNAMIC_FIELD_PARAMETERS } from "../dynamicFieldParameters";
import "./CreateEdaVersionForm.scss";

interface CreateEdaVersionFormProps {
  buttonContainer?: HTMLElement;
  defaultValues: EdaVersionFormValues;
  identityList: IdentityDefinitionList;
  isNewVersion?: boolean;
  prefillData?: PrefillData;
  referencedIdentityOptions: Record<string, Identity>;
  siteId: number;
  tagOptions: Array<Tag>;
  onSubmit: (data: EdaVersionFormValues) => Promise<void>;
  onToggle: () => void;
}

function CreateEdaVersionForm({
  buttonContainer,
  defaultValues,
  identityList,
  isNewVersion,
  prefillData,
  referencedIdentityOptions,
  siteId,
  tagOptions,
  onSubmit,
  onToggle
}: CreateEdaVersionFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const { persons: companies } = usePersons({ siteId });
  const { data: connections } = useConfiguratorComponents(
    ObjectName.Connection,
    siteId
  );
  const { data: consumers } = useConfiguratorComponents(
    ObjectName.Consumer,
    siteId
  );
  const { data: generators } = useConfiguratorComponents(
    ObjectName.Generator,
    siteId
  );
  const { data: meters } = useConfiguratorComponents(ObjectName.Meter, siteId);
  const { data: componentsBase } = useComponentsBase(siteId);
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    watch
  } = useForm<EdaVersionFormValues>({ defaultValues });

  const artDerEnergieMengeValue = watch(EDA_FORM_FIELDS.artDerEnergieMenge);
  const estimationProcedureValue = watch(EDA_FORM_FIELDS.estimationProcedure);
  const originValue = watch(EDA_FORM_FIELDS.origin);
  const stromsteuerpflichtigValue = watch(EDA_FORM_FIELDS.stromsteuerpflichtig);
  const parameters =
    artDerEnergieMengeValue &&
    identityList &&
    identityList[artDerEnergieMengeValue]
      ? Object.keys(identityList[artDerEnergieMengeValue])
      : [];

  function shouldShowReferenzenTitle() {
    return (
      parameters.filter((param) => param !== "displayName" && param !== "site")
        .length > 0
    );
  }

  function getArtDerEnergieMengeChoices(): ComboboxData {
    if (typeof identityList === "undefined") {
      return [];
    }

    return Object.entries(identityList)
      .reduce<Array<ComboboxItem>>((items, identity) => {
        const [key, value] = identity;
        const label = value.displayName;

        if (typeof label === "string") {
          return [...items, { label, value: key }];
        }

        return items;
      }, [])
      .filter((choice) => Object.keys(choice).length <= 3)
      .sort(sortComboboxItems);
  }

  const onValidSubmit: SubmitHandler<EdaVersionFormValues> = async (data) => {
    try {
      const response = await onSubmit(data);
      return response;
    } catch (error) {
      setErrorsFromResponseData<EdaVersionFormValues>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const referencedIdentityChoices = Object.keys(referencedIdentityOptions)
    .map<ComboboxItem>((key) => ({
      value: key,
      label: key
    }))
    .sort(sortComboboxItems);

  const sortedMeterOptions = meters
    ?.map<NumericComboboxItem>(({ id, name }) => ({
      value: id,
      label: name
    }))
    .sort(sortComboboxItems);

  const sortedCompanies = companies
    ?.map<NumericComboboxItem>((company) => ({
      value: company.id,
      label: company.name
    }))
    .sort(sortComboboxItems);

  const sortedComponentsBase =
    componentsBase
      ?.map<NumericComboboxItem>((component) => ({
        value: component.id,
        label: component.name
      }))
      .sort(sortComboboxItems) ?? [];

  const tags = tagOptions.map((tag) => ({
    value: tag.slug,
    label: tag.longName
  }));

  const sortedConnections = connections
    ?.map<NumericComboboxItem>(({ id, name }) => ({
      value: id,
      label: name
    }))
    .sort(sortComboboxItems);

  const sortedConsumers = consumers
    ?.map<NumericComboboxItem>(({ id, name }) => ({
      value: id,
      label: name
    }))
    .sort(sortComboboxItems);

  const sortedGenerators = generators
    ?.map<NumericComboboxItem>(({ id, name }) => ({
      value: id,
      label: name
    }))
    .sort(sortComboboxItems);

  const sortedMeters = meters
    ?.map<NumericComboboxItem>(({ id, name }) => ({
      value: id,
      label: name
    }))
    .sort(sortComboboxItems);

  const submitCallback = handleSubmit(onValidSubmit);

  return (
    <Box className="CreateEdaVersionForm">
      <form onSubmit={submitCallback}>
        <FormFieldController
          control={control}
          data={{
            ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
              EDA_FORM_FIELDS.artDerEnergieMenge
            ],
            data: getArtDerEnergieMengeChoices(),
            disabled: !!prefillData?.artDerEnergieMenge
          }}
          error={errors[EDA_FORM_FIELDS.artDerEnergieMenge]}
        />

        {shouldShowReferenzenTitle() && (
          <p className="section-label">Referenzen</p>
        )}

        <div className="referenzen-container">
          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.description) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                  EDA_FORM_FIELDS.description
                ],
                disabled: !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.description]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.component) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                  EDA_FORM_FIELDS.component
                ],
                data: sortedComponentsBase,
                disabled: !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.component]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.company) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.company],
                data: sortedCompanies,
                disabled:
                  !(Array.isArray(companies) && companies.length > 0) ||
                  !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.company]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.connection) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                  EDA_FORM_FIELDS.connection
                ],
                data: sortedConnections,
                disabled: !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.connection]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.consumer) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.consumer],
                data: sortedConsumers,
                disabled:
                  !(Array.isArray(consumers) && consumers.length > 0) ||
                  !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.consumer]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.generator) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                  EDA_FORM_FIELDS.generator
                ],
                data: sortedGenerators,
                disabled:
                  !(Array.isArray(generators) && generators.length > 0) ||
                  !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.generator]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.meter) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.meter],
                data: sortedMeters,
                disabled:
                  !(Array.isArray(meters) && meters.length > 0) || !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.meter]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.rootMeter) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                  EDA_FORM_FIELDS.rootMeter
                ],
                data: sortedMeters,
                disabled: !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.rootMeter]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.supplied) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.supplied],
                data: sortedCompanies,
                disabled: !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.supplied]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.supplier) && (
            <FormFieldController
              control={control}
              data={{
                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.supplier],
                data: sortedCompanies,
                disabled: !!prefillData
              }}
              error={errors[EDA_FORM_FIELDS.supplier]}
            />
          )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.toCompany) &&
            companies.length > 0 && (
              <FormFieldController
                control={control}
                data={{
                  ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                    EDA_FORM_FIELDS.toCompany
                  ],
                  data: sortedCompanies,
                  disabled:
                    !(Array.isArray(companies) && companies.length > 0) ||
                    !!prefillData
                }}
                error={errors[EDA_FORM_FIELDS.toCompany]}
              />
            )}

          {parameters.includes(DYNAMIC_FIELD_PARAMETERS.fromCompany) &&
            companies.length > 0 && (
              <FormFieldController
                control={control}
                data={{
                  ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                    EDA_FORM_FIELDS.fromCompany
                  ],
                  data: sortedCompanies,
                  disabled:
                    !(Array.isArray(companies) && companies.length > 0) ||
                    !!prefillData
                }}
                error={errors[EDA_FORM_FIELDS.fromCompany]}
              />
            )}
        </div>

        <FormFieldController
          control={control}
          data={CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.origin]}
          error={errors[EDA_FORM_FIELDS.origin]}
        />

        <FormFieldController
          control={control}
          data={CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.medium]}
          error={errors[EDA_FORM_FIELDS.medium]}
        />

        <FormFieldController
          control={control}
          data={{
            ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.validity],
            disabled:
              !isNewVersion &&
              Boolean(
                prefillData?.originalVersion.versions[0].firstDate &&
                prefillData?.originalVersion.versions[0].lastDate
              ),
            presets: { type: DateRangePresets.YearsAndMonths }
          }}
          error={
            errors[EDA_FORM_FIELDS.validity]?.[0] ||
            errors[EDA_FORM_FIELDS.validity]?.[1]
          }
        />

        {(originValue ?? prefillData?.origin) === ORIGIN_METER && (
          <FormFieldController
            control={control}
            data={{
              ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.meter],
              data: sortedMeterOptions
            }}
            error={errors[EDA_FORM_FIELDS.meter]}
          />
        )}

        {(originValue ?? prefillData?.origin) === ORIGIN_COMPUTATION && (
          <>
            <FormFieldController
              control={control}
              data={
                CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.frequency]
              }
              error={errors[EDA_FORM_FIELDS.frequency]}
            />
            <FormFieldController
              control={control}
              data={CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.formula]}
              error={errors[EDA_FORM_FIELDS.formula]}
            />
          </>
        )}

        {(originValue ?? prefillData?.origin) === ORIGIN_ESTIMATION && (
          <>
            <FormFieldController
              control={control}
              data={
                CREATE_EDA_VERSION_FORM_FIELD_DATA[
                  EDA_FORM_FIELDS.estimationProcedure
                ]
              }
              error={errors[EDA_FORM_FIELDS.estimationProcedure]}
            />

            <>
              {estimationProcedureValue &&
                ESTIMATION_PROCEDURE_FIELDS[estimationProcedureValue]?.map(
                  (field: string) => {
                    switch (field) {
                      case ESTIMATION_PROCEDURE_PARAMETER_ANNUAL_CONSUMPTION:
                        return (
                          <FormFieldController
                            control={control}
                            data={
                              CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.annualConsumption
                              ]
                            }
                            error={errors[EDA_FORM_FIELDS.annualConsumption]}
                            key={EDA_FORM_FIELDS.annualConsumption}
                          />
                        );

                      case ESTIMATION_PROCEDURE_PARAMETER_CONSTANT_LOAD_KW:
                        return (
                          <FormFieldController
                            control={control}
                            data={
                              CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.constantLoadKw
                              ]
                            }
                            error={errors[EDA_FORM_FIELDS.constantLoadKw]}
                            key={EDA_FORM_FIELDS.constantLoadKw}
                          />
                        );

                      case ESTIMATION_PROCEDURE_PARAMETER_NUMBER_OF_CONSUMERS:
                        return (
                          <FormFieldController
                            control={control}
                            data={
                              CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.numberOfConsumers
                              ]
                            }
                            error={errors[EDA_FORM_FIELDS.numberOfConsumers]}
                            key={EDA_FORM_FIELDS.numberOfConsumers}
                          />
                        );

                      case ESTIMATION_PROCEDURE_PARAMETER_RATIO_OF_ENERGY_DATA:
                        return (
                          <FormFieldController
                            control={control}
                            data={
                              CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.ratioOfEnergyData
                              ]
                            }
                            error={errors[EDA_FORM_FIELDS.ratioOfEnergyData]}
                            key={EDA_FORM_FIELDS.ratioOfEnergyData}
                          />
                        );

                      case ESTIMATION_PROCEDURE_PARAMETER_REFERENCED_EDA_IDENTITY:
                        return (
                          <FormFieldController
                            control={control}
                            data={{
                              ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.referencedEdaIdentity
                              ],
                              data: referencedIdentityChoices
                            }}
                            error={
                              errors[EDA_FORM_FIELDS.referencedEdaIdentity]
                            }
                            key={EDA_FORM_FIELDS.referencedEdaIdentity}
                          />
                        );

                      case ESTIMATION_PROCEDURE_PARAMETER_FREQUENCY:
                        return (
                          <FormFieldController
                            control={control}
                            data={
                              CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.frequency
                              ]
                            }
                            error={errors[EDA_FORM_FIELDS.frequency]}
                            key={EDA_FORM_FIELDS.frequency}
                          />
                        );
                      case ESTIMATION_PROCEDURE_PARAMETER_SECURITY_BUFFER:
                        return (
                          <FormFieldController
                            control={control}
                            data={
                              CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                EDA_FORM_FIELDS.securityBuffer
                              ]
                            }
                            error={errors[EDA_FORM_FIELDS.securityBuffer]}
                            key={EDA_FORM_FIELDS.securityBuffer}
                          />
                        );
                      case ESTIMATION_PROCEDURE_PARAMETER_COMPUTATION:
                        return (
                          <div key="computation">
                            <FormFieldController
                              control={control}
                              data={{
                                ...CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                  EDA_FORM_FIELDS.referencedEdaIdentity
                                ],
                                data: referencedIdentityChoices
                              }}
                              error={
                                errors[EDA_FORM_FIELDS.referencedEdaIdentity]
                              }
                              key={EDA_FORM_FIELDS.referencedEdaIdentity}
                            />

                            <FormFieldController
                              control={control}
                              data={
                                CREATE_EDA_VERSION_FORM_FIELD_DATA[
                                  EDA_FORM_FIELDS.formula
                                ]
                              }
                              error={errors[EDA_FORM_FIELDS.formula]}
                              key={EDA_FORM_FIELDS.formula}
                            />
                          </div>
                        );

                      default:
                        break;
                    }
                  }
                )}
            </>
          </>
        )}

        <FormFieldController
          control={control}
          data={CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.label]}
          error={errors[EDA_FORM_FIELDS.label]}
        />

        <FormFieldController
          control={control}
          data={
            CREATE_EDA_VERSION_FORM_FIELD_DATA[
              EDA_FORM_FIELDS.stromsteuerpflichtig
            ]
          }
          error={errors[EDA_FORM_FIELDS.stromsteuerpflichtig]}
        />

        {stromsteuerpflichtigValue && (
          <FormFieldController
            control={control}
            data={{
              ...CREATE_EDA_VERSION_FORM_FIELD_DATA[EDA_FORM_FIELDS.tags],
              data: tags
            }}
            error={errors[EDA_FORM_FIELDS.tags]?.[0]}
          />
        )}

        {buttonContainer &&
          ReactDOM.createPortal(
            <>
              <Button variant="default" onClick={onToggle}>
                Abbrechen
              </Button>
              <Button
                color="secondary"
                loading={isSubmitting}
                onClick={submitCallback}
              >
                Speichern
              </Button>
            </>,
            buttonContainer
          )}
      </form>
    </Box>
  );
}

export { CreateEdaVersionForm, CreateEdaVersionFormProps };
